import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { requiredValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { ResourceConfig } from "config/config";
import { executionLocationField } from "config/resources/sharedFieldsTyped";
import {
    DeleteListAction,
    PermanentlyDeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import { commentsTab, logsTab, logsTabKey, metadataTab } from "containers/Resources/screens/ResourcesSingle/constants";
import {
    buildSimpleColumn,
    isEnabledColumn,
} from "containers/Search/components/SearchTabs/columns";
import { getRelativeUrl } from "utils/service";
import identity from "./identity";
import resource from "./resource";
import { getLatestExecutions, getUrlDTO } from "./sharedFields";

interface WebhookSecurityProps {
    is_enabled: boolean;
    require_login: boolean;
    is_key_required: boolean;
}

const resourceName = "webhook";

const config: ResourceConfig = {
    label: "Webhook",
    icon: IconCollection.webhook,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, metadataTab.tab, commentsTab.tab],
                content: [logsTab.content, metadataTab.content, commentsTab.content],
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            ${identity.graphql.resourceBody}
            flow_id
            flow_id_flow {
                id
                name
            }
            require_login
            is_key_required
            url
            is_enabled
            is_productive
            last_activity
            execution_location
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            is_productive
            flow_id_flow {
                id
                name
            }
            execution_location
            url
            is_key_required
            require_login
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $flow_id: String,
            $is_productive: Boolean,
            $execution_location: String,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            flow_id: $flow_id,
            is_productive: $is_productive,
            execution_location: $execution_location,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $flow_id: String,
            $is_productive: Boolean,
            $execution_location: String,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            flow_id: $flow_id,
            is_productive: $is_productive,
            execution_location: $execution_location,
        `,
    },
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Productive",
            key: "is_productive",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="is_productive"
                        value={value.is_productive}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () => setNewValue({ field: "is_productive", value: value.is_productive })
                        : undefined
                ),
        },
        {
            label: "Flow",
            key: "flow",
            shown: true,
            dto: ({ flow_id_flow }: any) => {
                return (
                    flow_id_flow && (
                        <RecordLabel
                            link={getRelativeUrl("flow", flow_id_flow.id)}
                            name={flow_id_flow.name}
                            iconByType="flow"
                        />
                    )
                );
            },
        },
        {
            label: "Execution location",
            key: "execution_location",
            shown: false,
            sortable: true,
            dto: (value) =>
                buildSimpleColumn(
                    value?.execution_location,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "execution_location",
                                  value: value.execution_location,
                              })
                        : undefined
                ),
        },
        {
            label: "Url",
            key: "url",
            shown: false,
            sortable: false,
            dto: (value) =>
                buildSimpleColumn(
                    getUrlDTO(value?.url),
                    setNewValue ? () => setNewValue({ field: "url", value: value.url }) : undefined
                ),
        },
        {
            label: "Is key required",
            key: "is_key_required",
            shown: false,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="is_key_required"
                        value={value.is_key_required}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "is_key_required",
                                  value: value.is_key_required,
                              })
                        : undefined
                ),
        },
        {
            label: "Require login",
            key: "require_login",
            shown: false,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="require_login"
                        value={value.require_login}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () => setNewValue({ field: "require_login", value: value.require_login })
                        : undefined
                ),
        },
    ],
    statsSidebar: [getLatestExecutions("webhook")],
    form: [
        {
            name: "flow_id",
            component: "autocomplete",
            fieldProps: {
                label: "Flow",
                options: {
                    resource: "flow",
                },
                docs: {
                    text: "Choose a flow to call on this webhook",
                    urlPath: "/docs/webhooks#configuration",
                },
            },
            fieldAttr: {
                required: true,
            },
            validation: requiredValidator,
        },
        {
            name: "webhookSecurity",
            component: "webhookSecurity",
            fieldProps: {},
        },
        {
            name: "is_productive",
            component: "switch",
            fieldProps: {
                label: "Is productive",
                docs: {
                    text: "Does this webhook work in production",
                    urlPath: "/docs/webhooks#configuration",
                },
                defaultValue: true,
            },
        },
        {
            name: "url",
            component: "url",
            fieldProps: {
                label: "Url",
                asLink: true,
            },
        },
        executionLocationField({
            optionList: [
                { value: "flow_record", label: "Flow" },
                { value: "webhook_record", label: "Webhook" },
            ],
            docs: {
                text: "Where the executions started by this webhook will run",
                urlPath: "/docs/webhooks#configuration",
            },
        }),
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) => {
            const {
                is_enabled,
                is_key_required,
                require_login,
                ...rest
            } = resourceData ?? {};
            const webhookSecurity: WebhookSecurityProps = {
                is_enabled,
                is_key_required,
                require_login,
            };

            return { ...rest, webhookSecurity };
        },
    },
    detailsSidebar: [
        {
            label: "Last activity",
            valueKey: "last_activity",
            dto: (value: any) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
        },
    ],
};
export default config;
